app.component("appointmentSetting", {
	templateUrl: "app/src/views/templates/appointment-setting.tmpl.html",
	controller: "appointmentSettingController",
	bindings: { practiceId: "<" },
});

// Where the pointer last was during a drag, so a dropped merge field can be inserted under it.
// lvl-drop-target reports which editor took the drop but not where, and its dragover handler
// returns false - jQuery reads that as stopPropagation, so a listener on the document never sees
// the pointer while it is over an editor. Capture runs before that handler, hence the third
// argument. dragover only fires mid-drag, so this costs nothing the rest of the time.
app.factory("MergeFieldDropPoint", [
	"$document",
	function ($document) {
		const point = { x: null, y: null };

		$document[0].addEventListener(
			"dragover",
			function (e) {
				point.x = e.clientX;
				point.y = e.clientY;
			},
			true
		);

		return point;
	},
]);

// Carries a merge field's token on the drag. lvlDraggable would do this, except it sends the
// element's id and the same fields are rendered for both the email and the SMS composer - the two
// copies cannot share one id. The LVL events are still emitted so lvl-drop-target lights up the
// editors while a field is in flight. Note the drag effect is left alone: lvl-drop-target asks for
// "move", and pinning this to "copy" here would make the browser refuse the drop.
app.directive("mergeFieldDraggable", [
	"$rootScope",
	function ($rootScope) {
		return {
			restrict: "A",
			link: function (scope, el, attrs) {
				el.attr("draggable", "true");

				el.bind("dragstart", function (e) {
					(e.originalEvent || e).dataTransfer.setData("text", attrs.mergeFieldDraggable);
					$rootScope.$emit("LVL-DRAG-START");
				});

				el.bind("dragend", function () {
					$rootScope.$emit("LVL-DRAG-END");
				});
			},
		};
	},
]);

app.controller("appointmentSettingController", [
	"$scope",
	"$mdDialog",
	"AppointmentAPI",
	"Session",
	"$modal",
	"UserService",
	"FileUploader",
	"PracticeService",
	"ENV",
	"$window",
	"MergeFieldDropPoint",
	"$q",
	function ($scope, $mdDialog, AppointmentAPI, Session, $modal, UserService, FileUploader, PracticeService, ENV, $window, MergeFieldDropPoint, $q) {
		// The message composers open by default - editing the wording is the point of this screen.
		$scope.status = { open: true };
		$scope.status2 = { open: true };
		$scope.attachmentsOpen = false;

		// The logged-in user, as a number to match the ids the user list comes back with.
		let myId = null;

		// Placeholders swapped for real values when a reminder is sent. Every token here must have a
		// matching replace() in parseCustomText() in aws-lambda-appointment-reminder - a token the
		// lambda does not know about reaches the patient as literal text.
		// The relative times all follow one pattern, APPOINTMENT_TIME_MINUS_<minutes>_MIN, so the
		// lambda can derive them from the appointment start without a special case per token.
		const MERGE_FIELD = Object.freeze({
			PATIENT_NAME: "PATIENT_NAME",
			PRACTICE_NAME: "PRACTICE_NAME",
			PRACTICE_PHONE: "PRACTICE_PHONE",
			APPOINTMENT_DATE_TIME: "APPOINTMENT_DATE_TIME",
			APPOINTMENT_TIME_MINUS_60_MIN: "APPOINTMENT_TIME_MINUS_60_MIN",
			APPOINTMENT_TIME_MINUS_30_MIN: "APPOINTMENT_TIME_MINUS_30_MIN",
			APPOINTMENT_TIME_MINUS_15_MIN: "APPOINTMENT_TIME_MINUS_15_MIN",
		});

		// The time fields are the ones people misread - "15 min before" looks like wording that goes
		// into the message, when what arrives is a date and time. Every one of them is shown with a
		// worked example so the value, not the label, is what stands out.
		// Examples are built in the same dd/MM/yyyy hh:mm a format the lambda sends
		// (DateUtil.convertUTCToLocal), off a 9:00 AM appointment today so the date always reads as
		// current. Change this if that formatter changes, or the examples start telling fibs.
		function exampleTime(minutesBefore) {
			const at = new Date();
			at.setHours(9, 0, 0, 0);
			at.setMinutes(at.getMinutes() - minutesBefore);

			const pad = (value) => ("0" + value).slice(-2);
			const date = `${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${at.getFullYear()}`;
			const time = `${pad(at.getHours() % 12 || 12)}:${pad(at.getMinutes())}`;

			return `${date} ${time} ${at.getHours() < 12 ? "AM" : "PM"}`;
		}

		// Grouped so the relative times read as a set rather than as four more name-like fields.
		$scope.mergeFieldGroups = [
			{
				label: "Patient & practice",
				fields: [
					{ token: MERGE_FIELD.PATIENT_NAME, label: "Patient name", hint: "Becomes the patient's preferred name, or their first name" },
					{ token: MERGE_FIELD.PRACTICE_NAME, label: "Practice name", hint: "Becomes the name of this practice" },
					{ token: MERGE_FIELD.PRACTICE_PHONE, label: "Practice phone", hint: "Becomes this practice's phone number" },
				],
			},
			{
				label: "Appointment time",
				note: "These do not add the words on the field - each one becomes a date and time. The examples below them are for a 9:00 AM appointment.",
				fields: [
					{
						token: MERGE_FIELD.APPOINTMENT_DATE_TIME,
						label: "Appointment time",
						example: exampleTime(0),
						hint: "Becomes the date and time the appointment starts",
					},
					{
						token: MERGE_FIELD.APPOINTMENT_TIME_MINUS_60_MIN,
						label: "1 hour before",
						example: exampleTime(60),
						hint: "Becomes the date and time one hour before the appointment starts",
					},
					{
						token: MERGE_FIELD.APPOINTMENT_TIME_MINUS_30_MIN,
						label: "30 min before",
						example: exampleTime(30),
						hint: "Becomes the date and time 30 minutes before the appointment starts",
					},
					{
						token: MERGE_FIELD.APPOINTMENT_TIME_MINUS_15_MIN,
						label: "15 min before",
						example: exampleTime(15),
						hint: "Becomes the date and time 15 minutes before the appointment starts",
					},
				],
			},
		];

		this.$onInit = function () {
			$scope.API = AppointmentAPI;
			$scope.PracticeAPI = PracticeService;
			$scope.$mdDialog = $mdDialog;
			$scope.Session = Session;
			$scope.setting = null;
			$scope.applyToAllUserPractices = false;
			$scope.userName = $scope.Session.user.firstName || "" + $scope.Session.user.lastName || "";
			$scope.US = UserService;

			// Reminders are yours, but they are set up per practice - the rail says which.
			$scope.practiceId = this.practiceId || (Session.practice && Session.practice.id);
			const practice = (Session.practiceOptions || []).find((p) => p.id === $scope.practiceId) || Session.practice || {};
			$scope.userPractice = practice.name || "";

			const practiceFeatures = practice.practiceFeatures || {};
			$scope.appointmentEmailReminderFeature = practiceFeatures.appointmentEmailReminderFeature;
			$scope.appointmentSMSReminderFeature = practiceFeatures.appointmentSMSReminderFeature;

			$scope.sms_placeholder =
				"Hello [PATIENT_NAME],\n\n" +
				"This is a friendly reminder that you have booked an appointment with [PRACTICE_NAME] on [APPOINTMENT_DATE_TIME].\n\n" +
				"If you need to make any changes to your appointment, please contact the practice office at [PRACTICE_PHONE].";

			$scope.email_placeholder =
				"<p>Dear [PATIENT_NAME],</p><p>This is an automated reminder that you have an appointment scheduled with [PRACTICE_NAME] on [APPOINTMENT_DATE_TIME].</p><p><br></p><p>If you have any queries or need to make any changes to your appointment, please contact the practice office at [PRACTICE_PHONE].</p><p><br></p><p> We look forward to seeing you soon.</p><p><br></p><p> </p><p>Kind regards, </p><p>The [PRACTICE_NAME] Team</p>";

			$scope.saveStatus = "";

			// Reminders are sent using the settings of the appointment's clinician, and anyone at the practice
			// can set them up on a clinician's behalf. You are always on the list, even if you take no
			// appointments yourself.
			myId = Number(Session.user.id);
			$scope.reminderUser = { id: myId };
			$scope.reminderUsers = [{ id: myId, name: `${Session.user.firstName || ""} ${Session.user.lastName || ""}`.trim() + " (Me)" }];

			$q.all([fetchAppointmentCategory(), fetchReminderUsers()]).then(fetchSetting);
		};

		$scope.isOwnReminders = function () {
			return $scope.reminderUser.id === myId;
		};

		$scope.reminderUserName = function () {
			const user = $scope.reminderUsers.find((t) => t.id === $scope.reminderUser.id);
			return user ? user.name : "";
		};

		// Keeps the same appointment type open, so comparing two clinicians' reminders is one click.
		$scope.onReminderUserChange = function () {
			$scope.setting = null;
			$scope.settingOptions = [];
			$scope.saveStatus = "";
			// Applying to every practice is only for your own reminders - the API refuses it for anyone else.
			$scope.applyToAllUserPractices = false;
			fetchSetting();
		};

		function fetchReminderUsers() {
			return $scope.API.getAppointmentUsers($scope.practiceId).then(
				function (resp) {
					const colleagues = (resp.data || [])
						.filter((t) => t.id !== myId)
						.map((t) => ({ id: t.id, name: `${t.firstName || ""} ${t.lastName || ""}`.trim() }))
						.sort((a, b) => a.name.localeCompare(b.name));
					$scope.reminderUsers = $scope.reminderUsers.concat(colleagues);
				},
				function (error) {
					// Without the list you can still edit your own reminders.
					console.error(error && error.status);
				}
			);
		}

		var uploader = ($scope.uploader = new FileUploader({
			queueLimit: 1,
		}));

		$scope.change = function (contents) {};

		$scope.emailOptions = {
			// Do not steal focus - the composer is open by default and would scroll the pane.
			focus: false,
			disableResizeEditor: true,
			inheritPlaceholder: true,
			disableDragAndDrop: true,
			dialogsInBody: true,
			toolbar: [["insert", ["link"]]],
		};

		$scope.smsOptions = {
			height: 150,
			focus: false,
			disableResizeEditor: true,
			disableDragAndDrop: true,
			inheritPlaceholder: true,
			toolbar: []
		};

		function readFileInputEventAsArrayBuffer(file, callback) {
			var reader = new FileReader();

			reader.onload = function (loadEvent) {
				var arrayBuffer = loadEvent.target.result;
				callback(arrayBuffer);
			};

			reader.readAsArrayBuffer(file);
		}

		uploader.onAfterAddingFile = function (fileItem) {
			const type = fileItem._file.type;

			const fileInput = document.getElementById("uploadCaptureInputFile");
			if (fileInput) {
				fileInput.value = "";
			}
			$scope.uploader.clearQueue();

			let size = 0;

			if($scope.setting.file_data) {
				// file_data holds { file_name, file_size } records - summing the records themselves gave NaN,
				// which switched the 10MB check off.
				size =  $scope.setting.file_data.reduce((accumulator, currentValue) => accumulator + ((currentValue && currentValue.file_size) || 0), 0);
			}

			if (fileItem.file.size > (10 * 1024 * 1024 - size)) {
				return alert(`All files' maximum size is 10MB.`);
			}

			readFileInputEventAsArrayBuffer(fileItem._file, function (data) {
				var blobData = [data];
				var blob = new Blob(blobData, { type });

				$scope.API.isProcessing = true;
				$scope.API.uploadFile($scope.reminderUser.id, fileItem._file.name, $scope.setting.appointment_category_id, blob, $scope.practiceId)
					.then(
						function (resp) {
							fetchSetting();
						},
						function (error) {
							showErrorDialog();
						}
					)
					.then(() => ($scope.API.isProcessing = false));
			});
		};

		$scope.popoverDownload = {
			content: "Download",
		};

		$scope.popoverFileDelete = {
			content: "Delete",
		};

		// Where each composer's fields land. The container is the drop target - summernote claims an
		// isolate scope, so lvl-drop-target cannot sit on the editor element itself.
		const COMPOSERS = Object.freeze({
			email: { containerId: "reminderEmailTextContainer", editorId: "reminderEmailText", modelKey: "custom_email_text" },
			sms: { containerId: "reminderSmsTextContainer", editorId: "reminderSmsText", modelKey: "custom_sms_text" },
		});

		// The caret under the pointer. caretRangeFromPoint covers Chrome, Edge and Safari;
		// caretPositionFromPoint is the Firefox spelling and returns a node/offset pair instead.
		function caretRangeAt(x, y) {
			const doc = $window.document;

			if (doc.caretRangeFromPoint) {
				return doc.caretRangeFromPoint(x, y);
			}

			if (doc.caretPositionFromPoint) {
				const position = doc.caretPositionFromPoint(x, y);

				if (!position) {
					return null;
				}

				const caret = doc.createRange();
				caret.setStart(position.offsetNode, position.offset);
				caret.collapse(true);
				return caret;
			}

			return null;
		}

		// Moves the caret to where the field was dropped. summernote inserts at a range it caches
		// rather than at the live selection, so the new caret has to be handed to it: setLastRange()
		// with no argument re-reads the selection from the document. Returns false when the drop was
		// not over text - on the editor's padding, say - leaving the caller to fall back.
		function placeCaretAtDropPoint(editor, dropPoint) {
			if (!dropPoint || dropPoint.x === null) {
				return false;
			}

			// Not a descendant of the <summernote> element: summernote hides that and builds the
			// editor alongside it, so the editable comes from the context it stored instead.
			const editable = editor.data("summernote").layoutInfo.editable[0];
			const caret = caretRangeAt(dropPoint.x, dropPoint.y);

			if (!editable || !caret || !editable.contains(caret.startContainer)) {
				return false;
			}

			// Focus first: summernote focuses the editor before every command, and doing that after
			// the caret is set would move it off the drop point again.
			editable.focus();

			const selection = $window.getSelection();
			selection.removeAllRanges();
			selection.addRange(caret);

			editor.summernote("editor.setLastRange");
			return true;
		}

		// One insertion path for both clicking a field and dropping one in. A drop lands under the
		// pointer; a click lands wherever the cursor was left in that editor.
		function insertToken(token, target, dropPoint) {
			const composer = COMPOSERS[target];

			if (!token || !composer || !$scope.setting) {
				return;
			}

			const editor = angular.element("#" + composer.editorId);

			// Note: `editor.summernote` is always defined once the plugin is loaded, so it cannot be
			// used to tell an initialised editor from any other element - the data key can.
			try {
				if (editor.length && editor.data("summernote")) {
					if (!placeCaretAtDropPoint(editor, dropPoint)) {
						editor.summernote("editor.restoreRange");
						editor.summernote("editor.focus");
					}

					editor.summernote("editor.insertText", `[${token}] `);
					return;
				}
			} catch (e) {
				// Fall through to appending.
			}

			// The editor is not ready yet - append rather than lose the field.
			$scope.setting[composer.modelKey] = ($scope.setting[composer.modelKey] || "") + ` [${token}] `;
		}

		$scope.insertMergeField = function (field, target) {
			insertToken(field && field.token, target);
		};

		// lvl-drop-target reports the token the chip put on the drag, and the id of the element it is
		// on, which is the container. Drops happen outside a digest, hence the apply.
		$scope.onMergeFieldDrop = function (token, containerId) {
			const target = Object.keys(COMPOSERS).find((key) => COMPOSERS[key].containerId === containerId);
			insertToken(token, target, MergeFieldDropPoint);
			$scope.$applyAsync();
		};

		function download(data, filename, mime, bom) {
			// https://github.com/kennethjiang/js-file-download/blob/master/file-download.js
			var blobData = typeof bom !== "undefined" ? [bom, data] : [data];
			var blob = new Blob(blobData, { type: "application/pdf" });
			if (typeof window.navigator.msSaveBlob !== "undefined") {
				// IE workaround for "HTML7007: One or more blob URLs were
				// revoked by closing the blob for which they were created.
				// These URLs will no longer resolve as the data backing
				// the URL has been freed."
				window.navigator.msSaveBlob(blob, filename);
			} else {
				var blobURL = window.URL.createObjectURL(blob);
				var tempLink = document.createElement("a");
				tempLink.style.display = "none";
				tempLink.href = blobURL;
				tempLink.setAttribute("download", filename);

				// Safari thinks _blank anchor are pop ups. We only want to set _blank
				// target if the browser does not support the HTML5 download attribute.
				// This allows you to download files in desktop safari if pop up blocking
				// is enabled.
				if (typeof tempLink.download === "undefined") {
					tempLink.setAttribute("target", "_blank");
				}

				document.body.appendChild(tempLink);
				tempLink.click();
				document.body.removeChild(tempLink);
				window.URL.revokeObjectURL(blobURL);
				$scope.displaySpinner = false;
			}
		}

		$scope.downloadFile = function (name) {
			$scope.API.isProcessing = true;
			$scope.API.downloadFile($scope.reminderUser.id, name, $scope.setting.appointment_category_id, $scope.practiceId)
				.then(
					function (resp) {
						download(resp.data, name);
					},
					function (error) {
						showErrorDialog();
					}
				)
				.then(() => ($scope.API.isProcessing = false));
		};

		$scope.deleteFile = function (name) {
			$scope.API.isProcessing = true;
			$scope.API.deleteFile($scope.reminderUser.id, name, $scope.setting.appointment_category_id, $scope.practiceId)
				.then(
					function (resp) {
						fetchSetting();
						// download(resp.data, name);
					},
					function (error) {
						showErrorDialog();
					}
				)
				.then(() => ($scope.API.isProcessing = false));
		};

		function fetchAppointmentCategory() {
			$scope.PracticeAPI.isProcessing = true;
			return $scope.PracticeAPI.queryCalendarSetting($scope.practiceId)
				.then(
					function (resp) {
						$scope.appointment_categories = resp.data.appointment_categories;
					},
					function (error) {
						console.error(error && error.status);
					}
				)
				.then(function () {
					$scope.PracticeAPI.isProcessing = false;
				});
		}

		function connectData(data) {
			return data.category_reminder_settings.map((t) => {
				// The appointment types are undefined when their own fetch failed.
				const target = ($scope.appointment_categories || []).find((m) => m.category_id === t.appointment_category_id);
				return { ...t, name: target ? target.name : "unknown", id: t.appointment_category_id};
			});
		}

		$scope.settingId = { id: null };

		function fetchSetting() {
			const userId = $scope.reminderUser.id;
			// Someone else was picked while this was in flight - their own fetch is on the way, and owns the
			// spinner. Clearing it here would enable Save over an empty list and wipe their reminders.
			const isStale = () => userId !== $scope.reminderUser.id;

			$scope.API.isProcessing = true;
			$scope.API.getAppointmentReminderSetting(userId, $scope.practiceId)
				.then(
					function (response) {
						if (isStale()) {
							return;
						}
						$scope.settingOptions = connectData(response.data);
						// No appointment types yet means no settings - indexing [0] threw here, which skipped
						// the isProcessing reset below and left the Save button disabled. The type already
						// open stays open, including across a change of clinician.
						if (!$scope.settingOptions.some((t) => t.id === $scope.settingId.id) && $scope.settingOptions.length > 0) {
							$scope.settingId.id = $scope.settingOptions[0].id;
							$scope.onSettingCatalogChange();
						}
						$scope.onSettingCatalogChange();
					},
					function (error) {
						if (!isStale()) {
							showErrorDialog();
						}
					}
				)
				.then(() => {
					if (!isStale()) {
						$scope.API.isProcessing = false;
					}
				});
		}

		$scope.onSettingCatalogChange = function () {
			$scope.setting = $scope.settingOptions.find((t) => t.id === $scope.settingId.id);
			if ($scope.setting && !$scope.setting.days_to_remind_before_email) {
				$scope.setting.days_to_remind_before_email = 1;
			}
			if ($scope.setting && !$scope.setting.days_to_remind_before_sms) {
				$scope.setting.days_to_remind_before_sms = 1;
			}

			if ($scope.setting && !$scope.setting.custom_email_text) {
				$scope.setting.custom_email_text =  $scope.email_placeholder;
			}
			if ($scope.setting && !$scope.setting.custom_sms_text) {
				$scope.setting.custom_sms_text = $scope.sms_placeholder;
			}
		};

		// Saving keeps the settings dialog open so reminders for several appointment types
		// can be set up in one visit.
		$scope.save = function () {
			$scope.API.isProcessing = true;
			$scope.saveStatus = "Saving...";
			$scope.API.updateAppointmentReminderSetting($scope.reminderUser.id, $scope.applyToAllUserPractices, {
				category_reminder_settings: $scope.settingOptions,
			}, $scope.practiceId)
				.then(
					function (success) {
						$scope.saveStatus = "All reminders saved";
					},
					function (error) {
						$scope.saveStatus = "Error saving reminders";
						showErrorDialog();
					}
				)
				.then(() => ($scope.API.isProcessing = false));
		};

		$scope.positiveClick = () => {
			if ($scope.serverErrorModal) {
				$scope.serverErrorModal.hide();
				$scope.$mdDialog.hide();
			}
		};

		function showErrorDialog() {
			$scope.serverErrorModal = $modal({
				scope: $scope,
				templateUrl: "app/src/views/templates/modal.server-error.tpl.html",
				show: true,
				backdrop: "static",
				keyboard: false,
				title: "CatTrax has encountered an error",
			});
		}
	},
]);
